import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { TagSourcingService } from './tag-sourcing.service';

@Component({
    selector: 'gtw-tag-sourcing',
    templateUrl: './tag-sourcing.component.html',
    styleUrls: ['./tag-sourcing.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TagSourcingComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();

  private isInitialized = false;

  filterParams: any = {};
  baseURLs: any = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  rowData: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};

  names = ['Merge', 'Update', 'Insert'];
  transTypes = ['Book', 'Sch M', 'TBBS'];
  tagData: {
    tagKey: string;
    gieKey: string;
    actKey: string;
    combinationKey: string;
    dropdown: string;
    amount: string;
    transType: string;
  } = {
    tagKey: '',
    gieKey: '',
    actKey: '',
    combinationKey: '',
    dropdown: '',
    amount: '',
    transType: '',
  };
  showJsonLoader = false;
  isSaveClicked = false;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private tagSourcingService: TagSourcingService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG?.base_url) return;
    this.isInitialized = true;
    this.cdr.detectChanges();
  }

  save(): void {
    if (this.isSaveClicked) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--orange', true, 4);
      return;
    }
    this.isSaveClicked = true;
    this.showJsonLoader = true;
    this.cdr.detectChanges();

    this.tagSourcingService
      .saveTagData(
        this.GENERAL_CONFIG.base_url,
        this.tagData,
        this.globalService,
        this.filterParams,
        this.rowData
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (result: any) => {
          this.isSaveClicked = false;
          this.showJsonLoader = false;
          if (result?.errorMessage && result.errorMessage !== 'null') {
            this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red', true, 4);
          } else {
            this.gtwSnackbarService.openSnackBar('Data Saved Successfully.', 'gtw-snackbar--green', true, 4);
            this.requestGridUpdate.emit('refresh');
          }
          this.cdr.detectChanges();
        },
        (err) => {
          this.isSaveClicked = false;
          this.showJsonLoader = false;
          this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red', true, 4);
          this.cdr.detectChanges();
        }
      );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
